// The Machines Map's one tooltip as state (#6562): which square it describes, and what is
// HOLDING it open. `MachineMap` feeds it the squares' and the tooltip's events;
// `MachineMapTip` renders it.
//
// ONE CLAIM PER KIND, never one slot for all of them. The pointer (on a square, or on the
// tooltip it opened) and keyboard focus each name their own square, stamped when it was
// made, and the tooltip shows the NEWEST live claim. So the last thing the person did
// wins: Tab onto a square and it is described even with the mouse resting on another, and
// hover a square and it is described even while another has focus. When a claim goes, the
// tooltip falls back to the newest one left, so passing the pointer over a square never
// takes the tooltip from a focused one for good. A pointer claim held only by the
// pointer resting on the tooltip goes when a focus moves the tooltip away from it
// (`leaveMovedTip`). A claim on a square a re-render replaced (`isConnected` false) counts
// for nothing.
//
// WCAG 1.4.13 (content on hover or focus):
//   - hoverable: the pointer's claim is let go only {@link TIP_GRACE_MS} after it left both
//     the square and the tooltip, so the pointer can cross from the square onto it. The
//     pointer arriving on the tooltip of a focused square borrows that claim's
//     stamp, never a newer one, so it cannot keep the tooltip from the next square focused;
//   - persistent: focus keeps its claim until it leaves, so the tooltip always comes back
//     to it;
//   - dismissible: `close` drops every claim (Escape, a click outside), and the tooltip
//     stays shut until a new hover or focus.
//
// A PRESS IS NOT A CLAIM: selecting a square opens the machine's details panel for every
// viewer (#6563, owner ruling 2026-10-22), so there is no pin.

import { createMemo, createSignal, onCleanup } from 'solid-js';

/** A square the tooltip may describe: the machine's id and the square's element. */
export type TipTarget = { readonly id: string; readonly anchor: HTMLElement };

/** A target claimed at `seq`: a larger stamp is a later claim. */
type Claim = TipTarget & { readonly seq: number };

/** The pointer's claim: its square, and whether it is on the square, the tooltip, or both. */
type PointerClaim = Claim & { readonly onSquare: boolean; readonly onTip: boolean };

/** How long the pointer's claim outlives the pointer, so it can cross onto the tooltip. */
export const TIP_GRACE_MS = 120;

const live = <T extends Claim>(c: T | null): T | null => (c?.anchor.isConnected ? c : null);

const newest = (claims: readonly (Claim | null)[]): Claim | null => {
  let best: Claim | null = null;
  for (const c of claims) if (c !== null && (best === null || c.seq > best.seq)) best = c;
  return best;
};

export function createMapTip(): {
  /** The square the tooltip describes now, or null when it is shut. */
  readonly tip: () => TipTarget | null;
  readonly hover: (target: TipTarget, on: boolean) => void;
  /** The pointer entered (`true`) or left the tooltip of the square it describes. */
  readonly hoverTip: (on: boolean) => void;
  readonly focus: (target: TipTarget, on: boolean) => void;
  readonly close: () => void;
  /** Drop every claim on a square no longer on the page (a filter or search removed it). */
  readonly prune: () => void;
} {
  const [pointer, setPointer] = createSignal<PointerClaim | null>(null);
  const [focused, setFocused] = createSignal<Claim | null>(null);
  let stamps = 0;
  const stamp = (): number => {
    stamps += 1;
    return stamps;
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = (): void => {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
  };
  /** The pointer is on neither the square nor its tooltip: let the claim go after the grace. */
  const letGoLater = (): void => {
    cancel();
    timer = setTimeout(() => {
      timer = undefined;
      const p = pointer();
      if (p !== null && !p.onSquare && !p.onTip) setPointer(null);
    }, TIP_GRACE_MS);
  };
  const top = (): Claim | null => newest([live(pointer()), live(focused())]);
  // Compared by square, not by stamp: a newer claim on the square already shown changes
  // nothing on screen, so the tooltip is not re-rendered for it.
  const shown = createMemo<TipTarget | null>(top, null, {
    equals: (a, b) => a?.id === b?.id && a?.anchor === b?.anchor,
  });
  const hover = (target: TipTarget, on: boolean): void => {
    const p = pointer();
    if (on) {
      cancel();
      const same = p?.anchor === target.anchor;
      // Every arrival is a new claim: the pointer coming onto a square is the latest act.
      setPointer({ ...target, seq: stamp(), onSquare: true, onTip: same ? p.onTip : false });
      return;
    }
    if (p?.anchor !== target.anchor) return;
    setPointer({ ...p, onSquare: false });
    if (!p.onTip) letGoLater();
  };
  const hoverTip = (on: boolean): void => {
    const p = pointer();
    if (on) {
      const held = top();
      if (held === null) return;
      cancel();
      if (p?.anchor === held.anchor) {
        setPointer({ ...p, onTip: true });
        return;
      }
      // The tooltip of a focused square: the pointer borrows that claim's stamp.
      setPointer({ id: held.id, anchor: held.anchor, seq: held.seq, onSquare: false, onTip: true });
      return;
    }
    if (p === null) return;
    setPointer({ ...p, onTip: false });
    if (!p.onSquare) letGoLater();
  };
  /**
   * A new focus takes the tooltip to `anchor`. A pointer claim on another square that
   * the pointer is not on was held only by the pointer resting on the tooltip, which has now
   * moved away from it, and a browser fires no pointerleave for an element that moves out
   * from under a still cursor. Drop it, or it would reopen that tooltip later with nothing
   * holding it.
   */
  const leaveMovedTip = (anchor: HTMLElement): void => {
    const p = pointer();
    if (p !== null && !p.onSquare && p.anchor !== anchor) {
      cancel();
      setPointer(null);
    }
  };
  const focus = (target: TipTarget, on: boolean): void => {
    if (on) {
      leaveMovedTip(target.anchor);
      setFocused({ ...target, seq: stamp() });
    } else if (focused()?.anchor === target.anchor) setFocused(null);
  };
  const close = (): void => {
    cancel();
    setPointer(null);
    setFocused(null);
  };
  const prune = (): void => {
    if (live(pointer()) === null) setPointer(null);
    if (live(focused()) === null) setFocused(null);
  };
  onCleanup(cancel);
  return { tip: shown, hover, hoverTip, focus, close, prune };
}
